// API utility for all frontend API calls
const API_BASE_URL = process.env.REACT_APP_API_URL || '';

export const apiCall = async (endpoint, options = {}) => {
  const url = `${API_BASE_URL}${endpoint}`;

  // Don't set Content-Type for FormData - let browser handle it
  const isFormData = options.body instanceof FormData;
  const headers = {
    ...(isFormData ? {} : { 'Content-Type': 'application/json' }),
    ...options.headers,
  };

  const token = localStorage.getItem('token');
  if (token) {
    headers['Authorization'] = `Bearer ${token}`;
  }

  const response = await fetch(url, {
    ...options,
    headers,
  });

  // Redirect to login on 401 Unauthorized
  if (response.status === 401) {
    localStorage.removeItem('token');
    localStorage.removeItem('user');
    window.location.href = '/login';
    throw new Error('Session expired. Please login again.');
  }

  const data = await response.json();

  if (!response.ok) {
    const errorMessage = data.error || response.statusText || 'API error';
    throw new Error(errorMessage);
  }

  return data;
};

// ============================================
// V1 API Functions (XOrbit/VJA Pipeline)
// ============================================

// V1 Health Check
export const v1Health = async () => {
  return apiCall('/v1/health');
};

// V1 Get Available Models
export const v1GetModels = async () => {
  return apiCall('/v1/models');
};

// V1 Get Statistics (optionally filtered by model type)
export const v1GetStats = async (modelType = '') => {
  let url = '/v1/stats';
  if (modelType && modelType !== 'all') {
    url += `?model_type=${modelType}`;
  }
  return apiCall(url);
};

// V1 Get Work Orders with pagination and filtering
export const v1GetWorkOrders = async (params = {}) => {
  const { skip = 0, limit = 10, status, model_type, search } = params;
  let url = `/v1/work-orders?skip=${skip}&limit=${limit}`;
  if (status && status !== 'all') url += `&status=${status}`;
  if (model_type && model_type !== 'all') url += `&model_type=${model_type}`;
  if (search && search.trim()) url += `&search=${encodeURIComponent(search.trim())}`;
  return apiCall(url);
};

// V1 Get Single Work Order
export const v1GetWorkOrder = async (workOrderId) => {
  return apiCall(`/v1/work-orders/${workOrderId}`);
};

// V1 Create Work Order with file upload and extraction
export const v1Extract = async (file, modelType) => {
  const formData = new FormData();
  formData.append('file', file);
  formData.append('model', modelType); // 'xorbit' or 'vja'
  
  return apiCall('/v1/extract', {
    method: 'POST',
    body: formData,
  });
};

// V3 Create Work Order with file upload and extraction (Preprocessing + Qwen)
export const v3Extract = async (file) => {
  const formData = new FormData();
  formData.append('file', file);
  
  return apiCall('/v1/v3/extract', {
    method: 'POST',
    body: formData,
  });
};

// V4 Create Work Order with file upload and extraction (Textract + Qwen)
export const v4Extract = async (file) => {
  const formData = new FormData();
  formData.append('file', file);
  
  return apiCall('/v1/v4/extract', {
    method: 'POST',
    body: formData,
  });
};

// V1 Compare both models on same file
export const v1Compare = async (file) => {
  const formData = new FormData();
  formData.append('file', file);
  
  return apiCall('/v1/compare', {
    method: 'POST',
    body: formData,
  });
};

// V1 Delete Work Order
export const v1DeleteWorkOrder = async (workOrderId) => {
  return apiCall(`/v1/work-orders/${workOrderId}`, {
    method: 'DELETE',
  });
};

// V1 Get Work Order PDF URL (kept for backward compatibility)
export const v1GetWorkOrderPdfUrl = (workOrderId) => {
  return v1GetWorkOrderFileUrl(workOrderId);
};

// V1 Get Work Order File URL (original file - for download)
export const v1GetWorkOrderFileUrl = (workOrderId) => {
  if (API_BASE_URL) {
    return `${API_BASE_URL}/v1/work-orders/${workOrderId}/file`;
  }
  const port = window.location.port === '3000' ? '5001' : window.location.port;
  const protocol = window.location.protocol;
  const hostname = window.location.hostname;
  return `${protocol}//${hostname}:${port}/v1/work-orders/${workOrderId}/file`;
};

// V1 Get Work Order Preview URL (browser-viewable - converts TIFF to PNG with all pages)
export const v1GetWorkOrderPreviewUrl = (workOrderId) => {
  const baseUrl = API_BASE_URL || (() => {
    const port = window.location.port === '3000' ? '5001' : window.location.port;
    const protocol = window.location.protocol;
    const hostname = window.location.hostname;
    return `${protocol}//${hostname}:${port}`;
  })();
  return `${baseUrl}/v1/work-orders/${workOrderId}/preview`;
};

// V1 Get Work Order File Info (type, size, name)
export const v1GetWorkOrderFileInfo = async (workOrderId) => {
  return apiCall(`/v1/work-orders/${workOrderId}/file-info`);
};

// V1 Get Comparisons
export const v1GetComparisons = async (params = {}) => {
  const { skip = 0, limit = 10 } = params;
  return apiCall(`/v1/comparisons?skip=${skip}&limit=${limit}`);
};

// V1 Get Single Comparison
export const v1GetComparison = async (comparisonId) => {
  return apiCall(`/v1/comparisons/${comparisonId}`);
};

// V1 Delete Comparison
export const v1DeleteComparison = async (comparisonId) => {
  return apiCall(`/v1/comparisons/${comparisonId}`, { method: 'DELETE' });
};

// ============================================
// COCB API Functions (Change of Distributor Pipeline)
// ============================================

// COCB Get Statistics (uses frontend endpoint with JWT auth)
export const cocbGetStats = async () => {
  return apiCall('/v1/cocb/api/stats');
};

// COCB Get Work Orders with pagination (uses frontend endpoint with JWT auth)
export const cocbGetWorkOrders = async (params = {}) => {
  const { skip = 0, limit = 10, status, search } = params;
  let url = `/v1/cocb/api/work-orders?skip=${skip}&limit=${limit}`;
  if (status && status !== 'all') url += `&status=${status}`;
  if (search && search.trim()) url += `&search=${encodeURIComponent(search.trim())}`;
  return apiCall(url);
};

// COCB Get Single Work Order (uses frontend endpoint with JWT auth)
export const cocbGetWorkOrder = async (workOrderId) => {
  return apiCall(`/v1/cocb/api/work-orders/${workOrderId}`);
};

// COCB Extract Document (no schema needed - uses template)
export const cocbExtractDocument = async (file, uid = undefined) => {
  const formData = new FormData();
  formData.append('file', file);
  if (uid) {
    formData.append('uid', uid);
  }
  
  // Use frontend endpoint with JWT auth
  return apiCall('/v1/cocb/api/extract', {
    method: 'POST',
    body: formData,
  });
};

// COCB Delete Work Order
export const cocbDeleteWorkOrder = async (workOrderId) => {
  return apiCall(`/v1/cocb/api/work-orders/${workOrderId}`, {
    method: 'DELETE',
  });
};

// COCB Get Work Order File URL (frontend endpoint with JWT)
export const cocbGetWorkOrderFileUrl = (workOrderId) => {
  if (API_BASE_URL) {
    return `${API_BASE_URL}/v1/cocb/api/work-orders/${workOrderId}/file`;
  }
  const port = window.location.port === '3000' ? '5001' : window.location.port;
  const protocol = window.location.protocol;
  const hostname = window.location.hostname;
  return `${protocol}//${hostname}:${port}/v1/cocb/api/work-orders/${workOrderId}/file`;
};

// COCB Get Work Order Preview URL (frontend endpoint with JWT, converts TIFF to PNG)
export const cocbGetWorkOrderPreviewUrl = (workOrderId) => {
  const baseUrl = API_BASE_URL || (() => {
    const port = window.location.port === '3000' ? '5001' : window.location.port;
    const protocol = window.location.protocol;
    const hostname = window.location.hostname;
    return `${protocol}//${hostname}:${port}`;
  })();
  return `${baseUrl}/v1/cocb/api/work-orders/${workOrderId}/preview`;
};

// ============================================
// SIP Pipeline API Functions (SIP Form OCR Processing)
// ============================================

// SIP Get Statistics
export const sipGetStats = async () => {
  return apiCall('/sip/api/stats');
};

// SIP Get Work Orders with pagination and filtering
export const sipGetWorkOrders = async (params = {}) => {
  const { skip = 0, limit = 20, fund, success, search } = params;
  let url = `/sip/api/work-orders?skip=${skip}&limit=${limit}`;
  if (fund) url += `&fund=${fund}`;
  if (success !== undefined && success !== '') url += `&success=${success}`;
  if (search) url += `&search=${encodeURIComponent(search)}`;
  console.log('[API] sipGetWorkOrders URL:', url);
  return apiCall(url);
};

// SIP Get Single Work Order by execution_id
export const sipGetWorkOrder = async (executionId) => {
  return apiCall(`/sip/api/work-orders/${executionId}`);
};

// SIP Get Result by Barcode
export const sipGetResultByBarcode = async (barcode, fund = '') => {
  let url = `/sip/api/result/${barcode}`;
  if (fund) url += `?fund=${fund}`;
  return apiCall(url);
};

// SIP Process OCR (main processing endpoint)
export const sipProcessOCR = async (data) => {
  // data should contain: { fund, barcode (array or string), dump_s3_bucket (optional) }
  return apiCall('/sip/api/process', {
    method: 'POST',
    body: JSON.stringify(data),
  });
};

// SIP Upload File for OCR extraction
export const sipUploadFile = async (file, fund, uid = undefined) => {
  const formData = new FormData();
  formData.append('file', file);
  formData.append('fund', fund);
  if (uid) {
    formData.append('uid', uid);
  }
  
  // Use frontend endpoint with JWT auth
  return apiCall('/sip/api/upload', {
    method: 'POST',
    body: formData,
  });
};

// SIP Get Work Order File URL (for download)
export const sipGetWorkOrderFileUrl = (executionId) => {
  if (API_BASE_URL) {
    return `${API_BASE_URL}/sip/api/work-orders/${executionId}/file`;
  }
  const port = window.location.port === '3000' ? '5001' : window.location.port;
  const protocol = window.location.protocol;
  const hostname = window.location.hostname;
  return `${protocol}//${hostname}:${port}/sip/api/work-orders/${executionId}/file`;
};

// SIP Get Supported Funds
export const sipGetSupportedFunds = async () => {
  return apiCall('/sip/api/supported-funds');
};

// SIP Delete Work Order
export const sipDeleteWorkOrder = async (executionId) => {
  return apiCall(`/sip/api/work-orders/${executionId}`, {
    method: 'DELETE',
  });
};

// SIP Get Signatures by Account Number
export const sipGetSignatures = async (acno, fund = '') => {
  let url = `/sip/api/signatures/${acno}`;
  if (fund) url += `?fund=${fund}`;
  return apiCall(url);
};

// SIP Health Check
export const sipHealth = async () => {
  return apiCall('/sip/api/health');
};

// SIP Pipeline Info
export const sipInfo = async () => {
  return apiCall('/sip/api/info');
};

// ============================================
// COB API Functions (Change of Bank Pipeline)
// ============================================

// COB Get Statistics (optionally filtered by version)
export const cobGetStats = async (version = '') => {
  let url = '/cob/api/statistics';
  if (version && version !== 'all') {
    url += `?version=${version}`;
  }
  return apiCall(url);
};

// COB Get Work Orders with pagination and filtering
export const cobGetWorkOrders = async (params = {}) => {
  const { skip = 0, limit = 10, status, version, search } = params;
  
  // Use version-specific endpoint if version is provided
  let baseUrl = '/cob/api/work-orders';
  if (version === 'v1') {
    baseUrl = '/cob/api/v1/work-orders';
  } else if (version === 'v2') {
    baseUrl = '/cob/api/v2/work-orders';
  }
  
  let url = `${baseUrl}?skip=${skip}&limit=${limit}`;
  if (status && status !== 'all') url += `&status=${status}`;
  if (search && search.trim()) url += `&search=${encodeURIComponent(search.trim())}`;
  return apiCall(url);
};

// COB Get Single Work Order
export const cobGetWorkOrder = async (workOrderId) => {
  return apiCall(`/cob/api/work-orders/${workOrderId}`);
};

// COB Extract Document with version (v1=Gemini, v2=Qwen)
export const cobExtract = async (file, version = 'v2', uid = undefined) => {
  const formData = new FormData();
  formData.append('file', file);
  formData.append('version', version);
  if (uid) {
    formData.append('uid', uid);
  }
  
  return apiCall('/cob/api/extract', {
    method: 'POST',
    body: formData,
  });
};

// COB Extract with V1 (Gemini)
export const cobExtractV1 = async (file, uid = undefined) => {
  return cobExtract(file, 'v1', uid);
};

// COB Extract with V2 (Qwen)
export const cobExtractV2 = async (file, uid = undefined) => {
  return cobExtract(file, 'v2', uid);
};

// COB Extract with V3 (ChangeOfBank - Full Page Analysis with Bedrock)
export const cobExtractV3 = async (file, uid = undefined) => {
  const formData = new FormData();
  formData.append('file', file);
  if (uid) {
    formData.append('uid', uid);
  }
  
  return apiCall('/cob/api/v3/extract', {
    method: 'POST',
    body: formData,
  });
};

// COB V3 Get Work Orders with pagination and filtering
export const cobGetWorkOrdersV3 = async (params = {}) => {
  const { skip = 0, limit = 10, status, search } = params;
  let url = `/cob/api/v3/work-orders?skip=${skip}&limit=${limit}`;
  if (status && status !== 'all') url += `&status=${status}`;
  if (search && search.trim()) url += `&search=${encodeURIComponent(search.trim())}`;
  return apiCall(url);
};

// COB V3 Get Statistics
export const cobGetStatsV3 = async () => {
  return apiCall('/cob/api/v3/statistics');
};

// COB V3 Get Single Work Order
export const cobGetWorkOrderV3 = async (workOrderId) => {
  return apiCall(`/cob/api/v3/work-orders/${workOrderId}`);
};

// COB V3 Delete Work Order
export const cobDeleteWorkOrderV3 = async (workOrderId) => {
  return apiCall(`/cob/api/v3/work-orders/${workOrderId}`, {
    method: 'DELETE',
  });
};

// COB V3 Get Work Order File URL (for download)
export const cobGetWorkOrderFileUrlV3 = (workOrderId) => {
  if (API_BASE_URL) {
    return `${API_BASE_URL}/cob/api/v3/work-orders/${workOrderId}/file`;
  }
  const port = window.location.port === '3000' ? '5001' : window.location.port;
  const protocol = window.location.protocol;
  const hostname = window.location.hostname;
  return `${protocol}//${hostname}:${port}/cob/api/v3/work-orders/${workOrderId}/file`;
};

// COB V3 Get Work Order Preview URL (converts TIFF to PNG for browser viewing)
export const cobGetWorkOrderPreviewUrlV3 = (workOrderId) => {
  const baseUrl = API_BASE_URL || (() => {
    const port = window.location.port === '3000' ? '5001' : window.location.port;
    const protocol = window.location.protocol;
    const hostname = window.location.hostname;
    return `${protocol}//${hostname}:${port}`;
  })();
  return `${baseUrl}/cob/api/v3/work-orders/${workOrderId}/preview`;
};

// COB Delete Work Order
export const cobDeleteWorkOrder = async (workOrderId) => {
  return apiCall(`/cob/api/work-orders/${workOrderId}`, {
    method: 'DELETE',
  });
};

// COB Get Work Order File URL (for download)
export const cobGetWorkOrderFileUrl = (workOrderId) => {
  if (API_BASE_URL) {
    return `${API_BASE_URL}/cob/api/work-orders/${workOrderId}/file`;
  }
  const port = window.location.port === '3000' ? '5001' : window.location.port;
  const protocol = window.location.protocol;
  const hostname = window.location.hostname;
  return `${protocol}//${hostname}:${port}/cob/api/work-orders/${workOrderId}/file`;
};

// COB Get Work Order Preview URL (converts TIFF to PNG for browser viewing)
export const cobGetWorkOrderPreviewUrl = (workOrderId) => {
  const baseUrl = API_BASE_URL || (() => {
    const port = window.location.port === '3000' ? '5001' : window.location.port;
    const protocol = window.location.protocol;
    const hostname = window.location.hostname;
    return `${protocol}//${hostname}:${port}`;
  })();
  return `${baseUrl}/cob/api/work-orders/${workOrderId}/preview`;
};

// ============================================
// COB Process API Functions (Full Pipeline)
// ============================================

// COB Process - Full pipeline (OCR → Merge → Validate → Compare)
export const cobProcess = async (file, originalXml, version = 'v2', uid = undefined, options = {}) => {
  const formData = new FormData();
  formData.append('file', file);
  formData.append('original_xml', originalXml);
  formData.append('version', version);
  if (uid) formData.append('uid', uid);
  if (options.ihno) formData.append('ihno', options.ihno);
  if (options.barcode) formData.append('barcode', options.barcode);
  if (options.confidence) formData.append('confidence', 'true');
  if (options.coordinates) formData.append('coordinates', 'true');

  const endpoint = version === 'v1' ? '/cob/api/v1/process' : '/cob/api/v2/process';
  
  return apiCall(endpoint, {
    method: 'POST',
    body: formData,
  });
};

// COB Process V1 (Gemini)
export const cobProcessV1 = async (file, originalXml, uid = undefined, options = {}) => {
  return cobProcess(file, originalXml, 'v1', uid, options);
};

// COB Process V2 (Qwen)
export const cobProcessV2 = async (file, originalXml, uid = undefined, options = {}) => {
  return cobProcess(file, originalXml, 'v2', uid, options);
};

// COB Get Process Statistics (optionally filtered by version)
export const cobGetProcessStats = async (version = '') => {
  let url = '/cob/api/process/statistics';
  if (version && version !== 'all') {
    url += `?version=${version}`;
  }
  return apiCall(url);
};

// COB Get Process Work Orders with pagination and filtering
export const cobGetProcessWorkOrders = async (params = {}) => {
  const { skip = 0, limit = 10, status, version, search } = params;
  
  // Use version-specific endpoint if version is provided
  let baseUrl = '/cob/api/process/work-orders';
  if (version === 'v1') {
    baseUrl = '/cob/api/v1/process/work-orders';
  } else if (version === 'v2') {
    baseUrl = '/cob/api/v2/process/work-orders';
  }
  
  let url = `${baseUrl}?skip=${skip}&limit=${limit}`;
  if (status && status !== 'all') url += `&status=${status}`;
  if (search && search.trim()) url += `&search=${encodeURIComponent(search.trim())}`;
  return apiCall(url);
};

// COB Get Single Process Work Order
export const cobGetProcessWorkOrder = async (workOrderId, version = '') => {
  let url = `/cob/api/process/work-orders/${workOrderId}`;
  if (version) {
    url = `/cob/api/${version}/process/work-orders/${workOrderId}`;
  }
  return apiCall(url);
};

// COB Delete Process Work Order
export const cobDeleteProcessWorkOrder = async (workOrderId, version = '') => {
  let url = `/cob/api/process/work-orders/${workOrderId}`;
  if (version) {
    url = `/cob/api/${version}/process/work-orders/${workOrderId}`;
  }
  return apiCall(url, { method: 'DELETE' });
};

// COB Get Process Work Order File URL
export const cobGetProcessWorkOrderFileUrl = (workOrderId, version = '') => {
  const baseUrl = API_BASE_URL || (() => {
    const port = window.location.port === '3000' ? '5001' : window.location.port;
    const protocol = window.location.protocol;
    const hostname = window.location.hostname;
    return `${protocol}//${hostname}:${port}`;
  })();
  if (version) {
    return `${baseUrl}/cob/api/${version}/process/work-orders/${workOrderId}/file`;
  }
  return `${baseUrl}/cob/api/process/work-orders/${workOrderId}/file`;
};

export default API_BASE_URL;


// ============================================
// Multi-AMC API Functions (Multi-AMC Extraction Pipeline)
// ============================================

// Multi-AMC Health Check
export const multiAmcHealth = async () => {
  return apiCall('/api/extraction/multi-amc/health');
};

// Multi-AMC Get Statistics
export const multiAmcGetStats = async (params = {}) => {
  const { amc_code, form_type } = params;
  let url = '/api/extraction/multi-amc/statistics';
  const queryParams = [];
  if (amc_code) queryParams.push(`amc_code=${amc_code}`);
  if (form_type) queryParams.push(`form_type=${form_type}`);
  if (queryParams.length > 0) url += '?' + queryParams.join('&');
  return apiCall(url);
};

// Multi-AMC List AMCs
export const multiAmcGetAmcs = async (status = 'all') => {
  let url = '/api/extraction/multi-amc/amcs';
  if (status && status !== 'all') url += `?status=${status}`;
  return apiCall(url);
};

// Multi-AMC Get AMC Details
export const multiAmcGetAmc = async (amcCode) => {
  return apiCall(`/api/extraction/multi-amc/amcs/${amcCode}`);
};

// Multi-AMC Get Form Types
export const multiAmcGetFormTypes = async () => {
  return apiCall('/api/extraction/multi-amc/form-types');
};

// Multi-AMC Extract Document (main extraction with auto-detection)
export const multiAmcExtract = async (file, amcCode, formType = null, barcode = null) => {
  const formData = new FormData();
  formData.append('file', file);
  formData.append('amc_code', amcCode);
  if (formType) formData.append('form_type', formType);
  if (barcode) formData.append('barcode', barcode);
  
  return apiCall('/api/extraction/multi-amc/extract', {
    method: 'POST',
    body: formData,
  });
};

// Multi-AMC Extract Manual (both AMC and form type specified)
export const multiAmcExtractManual = async (file, amcCode, formType, barcode = null) => {
  const formData = new FormData();
  formData.append('file', file);
  formData.append('amc_code', amcCode);
  formData.append('form_type', formType);
  if (barcode) formData.append('barcode', barcode);
  
  return apiCall('/api/extraction/multi-amc/extract/manual', {
    method: 'POST',
    body: formData,
  });
};

// Multi-AMC Detect Form Type (preview mode)
export const multiAmcDetect = async (file, amcCode) => {
  const formData = new FormData();
  formData.append('file', file);
  formData.append('amc_code', amcCode);
  
  return apiCall('/api/extraction/multi-amc/detect', {
    method: 'POST',
    body: formData,
  });
};

// Multi-AMC Get Work Orders with pagination and filtering
export const multiAmcGetWorkOrders = async (params = {}) => {
  const { skip = 0, limit = 20, status, amc_code, form_type, search } = params;
  let url = `/api/extraction/multi-amc/work-orders?skip=${skip}&limit=${limit}`;
  if (status && status !== 'all') url += `&status=${status}`;
  if (amc_code) url += `&amc_code=${amc_code}`;
  if (form_type) url += `&form_type=${form_type}`;
  if (search && search.trim()) url += `&search=${encodeURIComponent(search.trim())}`;
  return apiCall(url);
};

// Multi-AMC Get Single Work Order
export const multiAmcGetWorkOrder = async (workOrderId) => {
  return apiCall(`/api/extraction/multi-amc/work-orders/${workOrderId}`);
};

// Multi-AMC Delete Work Order
export const multiAmcDeleteWorkOrder = async (workOrderId) => {
  return apiCall(`/api/extraction/multi-amc/work-orders/${workOrderId}`, {
    method: 'DELETE',
  });
};

// Multi-AMC Get Result by Barcode
export const multiAmcGetResultByBarcode = async (barcode, amcCode = null) => {
  let url = `/api/extraction/multi-amc/result/${barcode}`;
  if (amcCode) url += `?amc_code=${amcCode}`;
  return apiCall(url);
};

// Multi-AMC Get Work Order File URL (for download)
export const multiAmcGetWorkOrderFileUrl = (workOrderId) => {
  const baseUrl = API_BASE_URL || (() => {
    const port = window.location.port === '3000' ? '5001' : window.location.port;
    const protocol = window.location.protocol;
    const hostname = window.location.hostname;
    return `${protocol}//${hostname}:${port}`;
  })();
  const token = localStorage.getItem('token') || localStorage.getItem('authToken');
  const tokenParam = token ? `?token=${encodeURIComponent(token)}` : '';
  return `${baseUrl}/api/extraction/multi-amc/work-orders/${workOrderId}/file${tokenParam}`;
};

// Multi-AMC Get Work Order Preview URL (for browser viewing)
// Supports optional page parameter for multi-page TIFF files
export const multiAmcGetWorkOrderPreviewUrl = (workOrderId, page = null) => {
  const baseUrl = API_BASE_URL || (() => {
    const port = window.location.port === '3000' ? '5001' : window.location.port;
    const protocol = window.location.protocol;
    const hostname = window.location.hostname;
    return `${protocol}//${hostname}:${port}`;
  })();
  const token = localStorage.getItem('token') || localStorage.getItem('authToken');
  const params = new URLSearchParams();
  if (token) params.append('token', token);
  if (page !== null) params.append('page', page);
  const queryString = params.toString();
  return `${baseUrl}/api/extraction/multi-amc/work-orders/${workOrderId}/preview${queryString ? '?' + queryString : ''}`;
};

// Multi-AMC Get Work Order Pages Info (for multi-page files)
export const multiAmcGetWorkOrderPages = async (workOrderId) => {
  return apiCall(`/api/extraction/multi-amc/work-orders/${workOrderId}/pages`);
};


// ============================================
// SIP/OTM API Functions (SIP & OTM Mandate Form Extraction Pipeline)
// ============================================

// SIP/OTM Get Statistics
export const sipotmGetStats = async () => {
  return apiCall('/v1/sip-otm/api/stats');
};

// SIP/OTM Get Work Orders with pagination and filtering
export const sipotmGetWorkOrders = async (params = {}) => {
  const { skip = 0, limit = 20, status, search } = params;
  let url = `/v1/sip-otm/api/work-orders?skip=${skip}&limit=${limit}`;
  if (status && status !== 'all') url += `&status=${status}`;
  if (search && search.trim()) url += `&search=${encodeURIComponent(search.trim())}`;
  return apiCall(url);
};

// SIP/OTM Get Single Work Order
export const sipotmGetWorkOrder = async (workOrderId) => {
  return apiCall(`/v1/sip-otm/api/work-orders/${workOrderId}`);
};

// SIP/OTM Extract Document (AMC is auto-detected from document)
export const sipotmExtractDocument = async (file, uid = undefined) => {
  const formData = new FormData();
  formData.append('file', file);
  if (uid) {
    formData.append('uid', uid);
  }
  
  return apiCall('/v1/sip-otm/api/extract', {
    method: 'POST',
    body: formData,
  });
};

// SIP/OTM Delete Work Order
export const sipotmDeleteWorkOrder = async (workOrderId) => {
  return apiCall(`/v1/sip-otm/api/work-orders/${workOrderId}`, {
    method: 'DELETE',
  });
};

// SIP/OTM Get Work Order File URL (for download)
export const sipotmGetWorkOrderFileUrl = (workOrderId) => {
  const baseUrl = API_BASE_URL || (() => {
    const port = window.location.port === '3000' ? '5001' : window.location.port;
    const protocol = window.location.protocol;
    const hostname = window.location.hostname;
    return `${protocol}//${hostname}:${port}`;
  })();
  const token = localStorage.getItem('token') || localStorage.getItem('authToken');
  const tokenParam = token ? `?token=${encodeURIComponent(token)}` : '';
  return `${baseUrl}/v1/sip-otm/api/work-orders/${workOrderId}/file${tokenParam}`;
};

// SIP/OTM Get Work Order Preview URL (for browser viewing)
export const sipotmGetWorkOrderPreviewUrl = (workOrderId) => {
  const baseUrl = API_BASE_URL || (() => {
    const port = window.location.port === '3000' ? '5001' : window.location.port;
    const protocol = window.location.protocol;
    const hostname = window.location.hostname;
    return `${protocol}//${hostname}:${port}`;
  })();
  const token = localStorage.getItem('token') || localStorage.getItem('authToken');
  const tokenParam = token ? `?token=${encodeURIComponent(token)}` : '';
  return `${baseUrl}/v1/sip-otm/api/work-orders/${workOrderId}/preview${tokenParam}`;
};
